import { createContext, ReactNode, useContext } from 'react';
import { useWebSocketConfiguration } from './useWebSocketConfiguration';
import { useAppDispatch } from '../../configuration/setup/hooks';
import { UseWebSocket } from './websocket.types';
import { stationApi } from '../../services/stationApi';
import { missionApi } from '../../services/missionApi';
import { MISSION_STATUS_ORDER } from '../../constants';
import handleNotification from '../handleNotification';

const WebSocketContext = createContext<UseWebSocket | undefined>(undefined);

interface WebSocketProviderProps {
    children: ReactNode;
}

export const WebSocketProvider = ({ children }: WebSocketProviderProps) => {
    const dispatch = useAppDispatch();

    const ws = useWebSocketConfiguration({
        messageHandlers: {
            CHARGING_STATION: (payload) => {
                dispatch(
                    stationApi.util.updateQueryData('getStations', undefined, (draft) => {
                        const idx = draft.findIndex((s) => s.chargingStationId === payload.chargingStationId);
                        if (idx !== -1 && JSON.stringify(draft[idx]) !== JSON.stringify(payload)) {
                            draft[idx] = payload;
                        }
                    })
                );
            },
            MISSION_LIST: (payload) => {
                dispatch(
                    missionApi.util.updateQueryData('getMissions', undefined, (draft) => {
                        const idx = draft.findIndex((m) => m.missionId === payload.data.missionId);
                        if (idx !== -1) {
                            const status = payload.data.missionStatus as keyof typeof MISSION_STATUS_ORDER;
                            const next = {
                                ...draft[idx],
                                ...payload.data,
                                statusPriority: MISSION_STATUS_ORDER[status] ?? draft[idx].statusPriority,
                            };
                            if (JSON.stringify(draft[idx]) !== JSON.stringify(next)) {
                                Object.assign(draft[idx], next);
                            }
                        }
                    })
                );
            },
            NOTIFICATION: handleNotification,
        },
        onOpen: () => console.log('[WS] Connection opened'),
        onClose: () => console.log('[WS] Connection closed'),
        onError: (e) => console.error('[WS] Connection error', e),
    });

    return <WebSocketContext.Provider value={ws}>{children}</WebSocketContext.Provider>;
};

export const useWebSocket = () => {
    const context = useContext(WebSocketContext);
    if (!context) {
        throw new Error('useWebSocket must be used within a WebSocketProvider');
    }
    return context;
};
